import React, { useEffect, useState, useCallback } from "react";
import { fetchAllUsersWithPagination } from "../../services/adminUserService";
import { useNavigate } from "react-router-dom";
import UserSearchFilters from "./UserSearchFilters";
import UserTablePagination from "./UserTablePagination";
import WhatsAppMessageModal from "./WhatsAppMessageModal";

const EnhancedAdminUsersTable = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 20
  });
  const [filters, setFilters] = useState({
    search: "",
    sortBy: "lastLogin",
    sortDir: "desc"
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  
  const navigate = useNavigate();

  const loadUsers = useCallback(async (page = 0, newFilters = filters, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const response = await fetchAllUsersWithPagination({
        page,
        size: pageSize,
        ...newFilters
      });
      
      // Backend returns direct response structure
      setUsers(response.data.data);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalElements: response.data.totalElements,
        pageSize: response.data.pageSize
      });
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, []);

  const handleSearch = (searchFilters) => {
    setFilters(searchFilters);
    loadUsers(0, searchFilters);
  };

  const handlePageChange = (page, newPageSize = null) => {
    if (newPageSize && newPageSize !== pagination.pageSize) {
      // Page size changed, update pagination state and reload from page 0
      setPagination(prev => ({ ...prev, pageSize: newPageSize }));
      loadUsers(0, filters, newPageSize);
    } else {
      // Regular page change
      loadUsers(page);
    }
  };

  const handleShowMore = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleSendWhatsApp = (user) => {
    setSelectedUser(user);
    setShowWhatsAppModal(true);
  };

  const formatWhatsAppNumber = (number) => {
    if (!number) return "Not provided";
    // Remove any existing + signs and format properly
    const cleanNumber = number.replace(/\+/g, '');
    // Check if it's an Indian number (starts with 91)
    if (cleanNumber.startsWith('91') && cleanNumber.length === 12) {
      return `+91 ${cleanNumber.slice(2, 7)}-${cleanNumber.slice(7)}`;
    }
    // For other formats, just add + if not present
    return number.startsWith('+') ? number : `+${number}`;
  };

  if (loading && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="enhanced-users-table">
      <UserSearchFilters onSearch={handleSearch} filters={filters} />
      
      <div className="table-stats">
        <span>Total Users: {pagination.totalElements}</span>
        <span>WhatsApp Enabled: {users.filter(u => u.whatsappNumber).length}</span>
        <span>Consented: {users.filter(u => u.whatsappConsent).length}</span>
      </div>

      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Picture</th>
              <th>Name</th>
              <th>Email</th>
              <th>WhatsApp</th>
              <th>Consent</th>
              <th>Last Login</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  {user.picture ? (
                    <img src={user.picture} alt="avatar" className="user-avatar" />
                  ) : (
                    <div className="avatar-placeholder">N/A</div>
                  )}
                </td>
                <td>{user.name || "N/A"}</td>
                <td>{user.email}</td>
                <td className="whatsapp-number">
                  {formatWhatsAppNumber(user.whatsappNumber)}
                </td>
                <td>
                  <span className={`consent-badge ${user.whatsappConsent ? 'consented' : 'not-consented'}`}>
                    {user.whatsappConsent ? "✅ Yes" : "❌ No"}
                  </span>
                </td>
                <td>
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleString()
                    : "Never"}
                </td>
                <td>
                  <span className={`status-badge ${user.suspended ? 'suspended' : 'active'}`}>
                    {user.suspended ? "Suspended" : "Active"}
                  </span>
                </td>
                <td className="actions">
                  <button 
                    className="btn-primary"
                    onClick={() => handleShowMore(user.id)}
                  >
                    Details
                  </button>
                  {user.whatsappNumber && user.whatsappConsent && (
                    <button
                      className="btn-whatsapp"
                      onClick={() => handleSendWhatsApp(user)}
                    >
                      📱 WhatsApp
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserTablePagination
        pagination={pagination}
        onPageChange={handlePageChange}
        loading={loading}
      />

      {showWhatsAppModal && (
        <WhatsAppMessageModal
          user={selectedUser}
          onClose={() => setShowWhatsAppModal(false)}
        />
      )}
    </div>
  );
};

export default EnhancedAdminUsersTable;